import React, { useMemo, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styles from "./index.module.css";
import ReportItemQualifiedGroupOverview from "Reporting/Components/ReportItemQualifiedGroupOverview";

const getCellValue = (row, columnHeader, columnName) => {
  const value = row?.values?.[columnHeader]?.[columnName];

  if (value === null || value === undefined || value === "") {
    return "";
  }

  return Number(value) || 0;
};

const buildTotalRow = (rows = [], columnGroups = []) => {
  const values = {};

  columnGroups.forEach((group) => {
    values[group.columnHeader] = {};

    group.columns.forEach((column) => {
      const total = rows.reduce((sum, row) => {
        return (
          sum +
          (Number(getCellValue(row, group.columnHeader, column.columnName)) ||
            0)
        );
      }, 0);

      values[group.columnHeader][column.columnName] = total;
    });
  });

  return {
    qualifiedGroup: "Total",
    rowSort: Number.MAX_SAFE_INTEGER,
    values,
    isTotal: true,
  };
};

export default function ReportTableQualifiedGroupOverview({
  report,
  title,
  onSortedReportsChange,
}) {
  const PAGE_HEIGHT_PX = 425;
  const tableRef = useRef(null);

  const [pageBreakIndexes, setPageBreakIndexes] = useState([]);
  const [sortDirection, setSortDirection] = useState("asc");

  const columnGroups = useMemo(() => {
    return Array.isArray(report?.columnGroups)
      ? [...report.columnGroups].sort(
          (a, b) => (a.columnHeaderSort || 0) - (b.columnHeaderSort || 0)
        )
      : [];
  }, [report]);

  const allColumns = useMemo(() => {
    return columnGroups.flatMap((group) =>
      (group.columns || [])
        .slice()
        .sort((a, b) => (a.columnSort || 0) - (b.columnSort || 0))
        .map((column) => ({
          id: `${group.columnHeader}::${column.columnName}`,
          columnHeader: group.columnHeader,
          columnHeaderSort: group.columnHeaderSort,
          columnName: column.columnName,
          columnSort: column.columnSort,
        }))
    );
  }, [columnGroups]);

  const sortedRows = useMemo(() => {
    const rows = Array.isArray(report?.rows) ? [...report.rows] : [];

    rows.sort((a, b) => {
      const aVal = Number(a?.rowSort ?? 0);
      const bVal = Number(b?.rowSort ?? 0);

      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });

    return rows;
  }, [report, sortDirection]);

  const totalRow = useMemo(() => {
    if (!sortedRows.length) return null;
    return buildTotalRow(sortedRows, columnGroups);
  }, [sortedRows, columnGroups]);

  useEffect(() => {
    if (!report?.reportTitle) return;

    onSortedReportsChange?.(report.reportTitle, {
      ...report,
      rows: sortedRows,
    });
  }, [report, sortedRows, onSortedReportsChange]);

  const toggleSort = () => {
    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const calculateBreaks = (ref, setBreaks) => {
    if (!ref.current) {
      return;
    }

    const timeout = setTimeout(() => {
      const allElements = Array.from(
        ref.current.querySelectorAll(`.${styles.pageRowWrapper}`)
      );

      const newBreaks = [];
      const headerEl = ref.current.querySelector(`.${styles.header}`);
      const upperHeaderEl = ref.current.querySelector(`.${styles.upperHeader}`);
      const headerHeight = headerEl?.getBoundingClientRect().height || 0;
      const upperHeaderHeight =
        upperHeaderEl?.getBoundingClientRect().height || 0;

      let headerInfoHeight = 0;
      const parent = ref.current.parentElement;
      if (parent) {
        const headerInfoEl = parent.querySelector(`.${styles.headerInfo}`);
        if (headerInfoEl) {
          headerInfoHeight = headerInfoEl.getBoundingClientRect().height;
        }
      }

      let staticHeight = headerInfoHeight + headerHeight + upperHeaderHeight;

      if (headerInfoHeight === 0) {
        staticHeight += 50;
      }

      let heightSoFar = 0;
      let currentRowIndex = 0;
      let isFirstPage = true;

      allElements.forEach((el) => {
        const rect = el.getBoundingClientRect();

        if (isFirstPage) {
          heightSoFar += staticHeight;
          isFirstPage = false;
        }

        if (heightSoFar + rect.height > PAGE_HEIGHT_PX) {
          newBreaks.push(currentRowIndex);
          heightSoFar = rect.height;
        } else {
          heightSoFar += rect.height;
        }

        currentRowIndex++;
      });

      setPageBreakIndexes(newBreaks);
    }, 100);

    return () => clearTimeout(timeout);
  };

  useEffect(() => {
    return calculateBreaks(tableRef, setPageBreakIndexes);
  }, [sortedRows, totalRow, columnGroups]);

  if (!report) {
    return null;
  }

  const dataColumnWidth =
    allColumns.length > 0 ? `${74 / allColumns.length}%` : "0%";

  return (
    <div>
      <div className={styles.headerInfo}>
        <div className={styles.title}>
          <div className={styles.event}>{title || report.reportTitle}</div>
        </div>
      </div>

      <div className={styles.iboContainer}>
        <div className={styles.container} ref={tableRef}>
          <div className={styles.upperHeader}>
            <div className={styles.qualifiedGroupSpacer} />

            {columnGroups.map((group) => {
              const groupColumns = (group.columns || [])
                .slice()
                .sort((a, b) => (a.columnSort || 0) - (b.columnSort || 0));

              return (
                <div
                  key={group.columnHeader}
                  className={styles.headerGroup}
                  style={{
                    width: `calc(${dataColumnWidth} * ${groupColumns.length})`,
                  }}
                >
                  <span className={styles.headerLabel}>
                    {group.columnHeader}
                  </span>
                </div>
              );
            })}
          </div>

          <div className={styles.header}>
            <div className={styles.qualifiedGroup} onClick={toggleSort}>
              Qualified Group
            </div>

            {allColumns.map((col, index) => {
              const isFirstInGroup =
                index === 0 ||
                allColumns[index - 1]?.columnHeader !== col.columnHeader;

              return (
                <div
                  key={col.id}
                  className={styles.col}
                  style={{
                    width: dataColumnWidth,
                    borderLeft: isFirstInGroup
                      ? "2px solid #000"
                      : "1px solid #e0e0e0",
                  }}
                >
                  {col.columnName}
                </div>
              );
            })}
          </div>

          {totalRow && (
            <React.Fragment key="total-row">
              {pageBreakIndexes.includes(sortedRows.length) && (
                <div className={styles.pageBreak} />
              )}
              <div className={styles.pageRowWrapper}>
                <ReportItemQualifiedGroupOverview
                  item={totalRow}
                  isOdd={sortedRows.length % 2 !== 0}
                  type="total"
                  allColumns={allColumns}
                  dataColumnWidth={dataColumnWidth}
                />
              </div>
            </React.Fragment>
          )}

          {sortedRows.map((row, i) => (
            <React.Fragment key={`${row.qualifiedGroup}-${row.rowSort}-${i}`}>
              {pageBreakIndexes.includes(i) && (
                <div className={styles.pageBreak} />
              )}
              <div className={styles.pageRowWrapper}>
                <ReportItemQualifiedGroupOverview
                  item={row}
                  isOdd={i % 2 !== 0}
                  type="row"
                  allColumns={allColumns}
                  dataColumnWidth={dataColumnWidth}
                />
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

ReportTableQualifiedGroupOverview.propTypes = {
  report: PropTypes.shape({
    reportTitle: PropTypes.string,
    columnGroups: PropTypes.arrayOf(
      PropTypes.shape({
        columnHeader: PropTypes.string,
        columnHeaderSort: PropTypes.number,
        columns: PropTypes.arrayOf(
          PropTypes.shape({
            columnName: PropTypes.string,
            columnSort: PropTypes.number,
          })
        ),
      })
    ),
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        qualifiedGroup: PropTypes.string,
        rowSort: PropTypes.number,
        values: PropTypes.object,
      })
    ),
  }),
  title: PropTypes.string,
  onSortedReportsChange: PropTypes.func,
};
